import { billingService } from '../../services/billing'
import loopback from '../../services/loopback'
import { friendlyPlainText } from '../../utils/error-message'
import { getOffers } from '../../services/pricing-offers'
import { AI_MODEL_DEFAULT } from '../../utils/ai-model-labels'
import { PAYMENT_METHODS, billingTypeForPaymentMethod } from '../../utils/billing-type'

const POLL_INTERVAL_MS = 2500
const POLL_TIMEOUT_MS = 90000
const CANCELLATION_NOTE = '  - Documento cancelado automaticamente pelo Portal API4COM por substituição decorrente da emissão de uma nova fatura atualizada.'

// TEL-2243: esta funcao devolvia a mensagem do billing-service em ingles, que ia
// direto para o modal de pagamento. Agora usa o texto ja traduzido pelo
// normalizador (o `billing.js` pendura `friendly` no erro); quando o codigo nao e
// conhecido, cai no texto que a propria chamada define — nunca no do backend.
function getBillingErrorMessage (error, defaultMessage) {
  // `friendlyPlainText` resolve o marcador `{suporte}`; um `lines.join(' ')` aqui
  // entregaria a chave literal, porque este destino nao passa pelo ErrorAlert.
  return friendlyPlainText(error, defaultMessage)
}

function getArrayFromResponse (response, additionalKeys = []) {
  if (Array.isArray(response)) return response
  if (response === null || response === undefined || typeof response !== 'object') return []
  const keys = ['data', ...additionalKeys]
  for (const key of keys) {
    if (response[key] && Array.isArray(response[key])) return response[key]
  }
  return []
}

function extractPaymentLink (payload) {
  if (payload === null || payload === undefined || typeof payload !== 'object') return null
  const invoiceUrl = payload.invoiceUrl
  if (typeof invoiceUrl === 'string' && invoiceUrl.trim().startsWith('http')) {
    return invoiceUrl.trim()
  }
  const externalUrl = payload.externalUrl
  if (typeof externalUrl === 'string' && externalUrl.trim().startsWith('http')) {
    return externalUrl.trim()
  }
  return null
}

function validateAccountAndGetIds (context) {
  const organization = context.$store.state.auth.organization
  const accountId = organization && organization.uuid
  if (!accountId) {
    context.subscriptionError = 'Conta não identificada (é necessário o UUID da conta). Verifique se a API retorna o campo uuid em GET /accounts.'
    return null
  }
  return { organization, accountId }
}

// O pbxapi lê a integração do Asaas da organização e vincula a conta no billing.
async function createAccountConfig (financeEmails, context) {
  try {
    await billingService.createAccountConfig({ financeEmails })
    return true
  } catch (error) {
    context.subscriptionError = getBillingErrorMessage(error, 'Erro ao configurar conta no billing.')
    return false
  }
}

function collectSelectedUserEmails (groupedUsers) {
  const emails = []
  if (groupedUsers === null || groupedUsers === undefined || typeof groupedUsers !== 'object') {
    return emails
  }
  Object.keys(groupedUsers).forEach(planCode => {
    const users = groupedUsers[planCode]
    if (!Array.isArray(users)) return
    users.forEach(user => {
      const email = user && user.email ? String(user.email).trim().toLowerCase() : ''
      if (email !== '' && !emails.includes(email)) {
        emails.push(email)
      }
    })
  })
  return emails
}

async function fetchApiUsersAndBuildUserMap (context, groupedUsers) {
  try {
    const selectedEmails = collectSelectedUserEmails(groupedUsers)
    if (selectedEmails.length === 0) {
      context.subscriptionError = 'Nenhum usuário com email encontrado para gerar cobrança.'
      return null
    }
    const query = selectedEmails
      .map(email => `filter[where][email][inq][]=${encodeURIComponent(email)}`)
      .join('&')
    const usersResponse = await loopback.get(`/users?filter[order]=name asc&${query}`)
    const apiUsers = getArrayFromResponse(usersResponse)
    if (apiUsers.length === 0) {
      context.subscriptionError = 'Nenhum usuário encontrado para gerar cobrança.'
      return null
    }
    const userUuidByKeyMap = new Map()
    apiUsers.forEach(user => {
      if (user.uuid) {
        const email = user.email ? String(user.email).trim().toLowerCase() : ''
        if (email) userUuidByKeyMap.set(email, user.uuid)
        const extension = user.extension ? String(user.extension).trim() : ''
        if (extension) userUuidByKeyMap.set(extension, user.uuid)
      }
    })
    return { userUuidByKeyMap }
  } catch (error) {
    context.subscriptionError = getBillingErrorMessage(error, 'Erro ao carregar usuários.')
    return null
  }
}

function getOfferPriceTableName (offer) {
  if (!offer || typeof offer !== 'object') return null
  if (!offer.pricingTable || typeof offer.pricingTable !== 'object') return null
  if (typeof offer.pricingTable.name !== 'string') return null
  const priceTableName = offer.pricingTable.name.trim()
  if (priceTableName === '') return null
  return priceTableName
}

// Os cards e o checkout consultam o billing em momentos diferentes. Se a tabela mudou
// no meio (pbxapi oscilou na elegibilidade da campanha, ou a janela da campanha fechou),
// o checkout gravaria um preço diferente do que o cliente viu: para e pede para recarregar.
const PRICES_CHANGED_MESSAGE = 'Os preços mudaram. Recarregue a página para ver os valores atualizados.'

function pricesChangedSinceCards (context, planCode, priceTableName) {
  const cards = context && Array.isArray(context.pricingList) ? context.pricingList : []
  const code = String(planCode).trim().toLowerCase()
  const card = cards.find(item => item && String(item.code).trim().toLowerCase() === code)
  // Sem card (ou card sem tabela) não há com o que comparar: segue como antes.
  if (!card || !card.priceTableName) return false
  return card.priceTableName !== priceTableName
}

function getOfferPlanName (offer) {
  if (!offer || !offer.plan || typeof offer.plan.name !== 'string') return null
  const planName = offer.plan.name.trim()
  if (planName === '') return null
  return planName
}

async function fetchOffersByPlanCode (context) {
  try {
    // Catálogo de contratação da conta (`purpose: 'contract'`): a vigente, ou a campanha
    // (Black November) se a conta for elegível — nunca a tabela antiga de uma conta que
    // chegou aqui ainda com assinatura ativa (suspensa pela régua). A tabela da oferta é a
    // que vai gravada na assinatura, então tem que ser a mesma dos cards (Pricing.vue).
    const offers = await getOffers({ purpose: 'contract' })
    const activeOffers = Array.isArray(offers) ? offers.filter(offer => offer && offer.active && offer.plan && offer.plan.name) : []
    if (activeOffers.length === 0) {
      context.subscriptionError = 'Nenhuma oferta ativa encontrada para gerar cobrança.'
      return null
    }
    const offersByPlanCode = new Map()
    activeOffers.forEach(offer => {
      const planCode = String(offer.plan.name).trim().toLowerCase()
      if (planCode !== '' && !offersByPlanCode.has(planCode)) {
        offersByPlanCode.set(planCode, offer)
      }
    })
    return { offersByPlanCode }
  } catch (error) {
    context.subscriptionError = getBillingErrorMessage(error, 'Erro ao carregar ofertas de preço.')
    return null
  }
}

// Licenças adicionais reservadas na contratação: assento SEM `userId`. É capacidade
// contratada e cobrada desde a primeira fatura, que fica não atribuída em Usuários até
// alguém ser convidado — sem cobrança nova nesse momento.
function reservedLicensesByPlanCode (context) {
  const reserved = context && context.reservedLicenses ? context.reservedLicenses : {}
  const byPlanCode = {}
  Object.keys(reserved).forEach(planCode => {
    const quantity = Number(reserved[planCode])
    if (Number.isInteger(quantity) && quantity > 0) byPlanCode[planCode] = quantity
  })
  return byPlanCode
}

function buildSubscriptionItems (groupedUsers, userUuidByKeyMap, offersByPlanCode, context) {
  const items = []
  if (groupedUsers === null || groupedUsers === undefined || typeof groupedUsers !== 'object') {
    context.subscriptionError = 'Nenhum plano com usuários para gerar cobrança.'
    return null
  }
  const licensesByPlanCode = reservedLicensesByPlanCode(context)
  const aiDropdownEnabled = !!(context && context.isRolloutAiDropdown)
  const rawAiModel = aiDropdownEnabled && context && typeof context.selectedAiModel === 'string'
    ? context.selectedAiModel.trim()
    : ''
  const aiDefaultName = rawAiModel !== '' ? rawAiModel : AI_MODEL_DEFAULT
  const planCodes = Object.keys(groupedUsers)
  Object.keys(licensesByPlanCode).forEach(planCode => {
    if (!planCodes.includes(planCode)) planCodes.push(planCode)
  })
  for (const planCode of planCodes) {
    const users = groupedUsers[planCode] || []
    const licenses = licensesByPlanCode[planCode] || 0
    if (users.length === 0 && licenses === 0) continue
    const offer = offersByPlanCode.get(String(planCode).trim().toLowerCase())
    if (!offer) {
      context.subscriptionError = `Oferta de preço não encontrada para o plano "${planCode}".`
      return null
    }
    const priceTableName = getOfferPriceTableName(offer)
    if (!priceTableName) {
      context.subscriptionError = `Tabela de preço não encontrada para o plano "${planCode}".`
      return null
    }
    if (pricesChangedSinceCards(context, planCode, priceTableName)) {
      context.subscriptionError = PRICES_CHANGED_MESSAGE
      return null
    }
    const seats = []
    for (const user of users) {
      const email = user.email ? String(user.email).trim().toLowerCase() : ''
      const extension = user.extension ? String(user.extension).trim() : ''
      const key = email !== '' ? email : extension
      const userUuid = key !== '' ? userUuidByKeyMap.get(key) : null
      if (!userUuid) {
        context.subscriptionError = 'Cada assento deve ter userId em formato UUID. Não foi possível identificar todos os usuários.'
        return null
      }
      seats.push({ userId: userUuid })
    }
    for (let reserved = 0; reserved < licenses; reserved++) {
      seats.push({})
    }
    const planName = getOfferPlanName(offer)
    if (!planName) {
      context.subscriptionError = `Nome do plano não encontrado na oferta para "${planCode}".`
      return null
    }
    const item = {
      planName,
      priceTableName,
      discount: 0,
      currency: 'BRL',
      seats
    }
    if (offer.hasAi === true) {
      item.aiDefaultName = aiDefaultName
    }
    items.push(item)
  }
  if (items.length === 0) {
    context.subscriptionError = 'Nenhum plano com usuários ou licenças para gerar cobrança.'
    return null
  }
  return { items }
}

function parseInvoiceResult (invoice) {
  if (invoice == null) return { paymentLink: null, netValue: undefined, dueDate: undefined }
  const paymentLink = extractPaymentLink(invoice)
  const netValue = invoice.netValue != null ? invoice.netValue : undefined
  const dueDate = invoice.dueDate != null ? invoice.dueDate : undefined
  return { paymentLink, netValue, dueDate }
}

async function waitForPaymentLink (context) {
  const startedAt = Date.now()
  context.invoiceLoadingLabel = 'Aguardando fatura...'

  while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
    try {
      const invoice = await billingService.getOpenInvoice()
      const result = parseInvoiceResult(invoice)
      if (result.paymentLink) return result
    } catch (pollError) {
    }
    await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS))
  }

  context.subscriptionError = 'Cobrança ainda não disponível. Tente novamente em instantes ou verifique seu e-mail.'
  return null
}

async function createSubscriptionAndWaitForPaymentLink (payload, context) {
  try {
    const subscriptionResponse = await billingService.contractSubscription(payload)
    const paymentLink = extractPaymentLink(subscriptionResponse)
    if (paymentLink) {
      const netValue = subscriptionResponse != null && subscriptionResponse.netValue != null ? subscriptionResponse.netValue : undefined
      const dueDate = subscriptionResponse != null && subscriptionResponse.dueDate != null ? subscriptionResponse.dueDate : undefined
      return { paymentLink, netValue, dueDate }
    }
    return await waitForPaymentLink(context)
  } catch (error) {
    // O financeiro não respondeu a tempo: a assinatura pode ter sido criada do outro
    // lado mesmo assim. Procura a cobrança em vez de mandar o cliente clicar de novo —
    // a nova tentativa cancelaria a cobrança recém-criada e geraria outra.
    if (error && error.errorCode === 'BILLING_UNAVAILABLE') {
      return waitForPaymentLink(context)
    }
    context.subscriptionError = getBillingErrorMessage(error, 'Erro ao gerar cobrança.')
    return null
  }
}

export async function runSubscriptionCheckout (context, confirmPayload) {
  if (confirmPayload === null || confirmPayload === undefined || typeof confirmPayload !== 'object') {
    return { success: false }
  }

  if (!validateAccountAndGetIds(context)) return { success: false }

  const rawMethod = confirmPayload.selectedPaymentMethod
  if (!PAYMENT_METHODS.includes(rawMethod)) {
    context.subscriptionError = 'Método de pagamento inválido.'
    return { success: false }
  }
  const billingType = billingTypeForPaymentMethod(rawMethod)

  let financeEmails = ''
  if (confirmPayload.financeEmails != null && confirmPayload.financeEmails !== undefined) {
    financeEmails = String(confirmPayload.financeEmails).trim()
  }

  const accountConfigCreated = await createAccountConfig(financeEmails, context)
  if (!accountConfigCreated) return { success: false }

  const usersResult = await fetchApiUsersAndBuildUserMap(context, context.groupedUsersBySelectedPlan)
  if (!usersResult) return { success: false }
  const { userUuidByKeyMap } = usersResult

  const offersResult = await fetchOffersByPlanCode(context)
  if (!offersResult) return { success: false }
  const { offersByPlanCode } = offersResult

  const grouped = context.groupedUsersBySelectedPlan
  const itemsResult = buildSubscriptionItems(grouped, userUuidByKeyMap, offersByPlanCode, context)
  if (!itemsResult) return { success: false }
  const { items } = itemsResult

  // Conta, descrição, ciclo e modelo de cobrança o pbxapi preenche pela sessão.
  const dueDay = Math.min(28, Math.max(1, context.effectiveBillingDay))
  const payload = {
    billingType,
    dueDay,
    items,
    cancellationNote: CANCELLATION_NOTE
  }

  const result = await createSubscriptionAndWaitForPaymentLink(payload, context)
  if (result === null) return { success: false }

  return { success: true, paymentLink: result.paymentLink, netValue: result.netValue, dueDate: result.dueDate }
}



// WEBPACK FOOTER //
// ./src/components/Subscription/subscription-checkout.js